import React, { createContext, useEffect, useState } from 'react';
import { useIsMutating, useQuery } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { useAPI, useGetUser } from 'hooks';
import PropTypes from 'prop-types';
import { toast } from 'sonner';
import Urls from 'data/Urls';

const CONST_DEFAULT_PRE_REGISTRATION_FEE = 200000;
const CONST_DEFAULT_MINIMUM_PILGRIMS = 200;
const CONST_ELIGIBILITY_AGE = 2;
const CONST_DEFAULT_RELIGIOUS_SUPERVISOR_QUOTA = 58;
const CONST_DEFAULT_MEDICAL_SUPERVISOR_QUOTA = 12;
const CONST_DEFAULT_RELIGIOUS_SUPERVISOR_RATIO = 16;
const CONST_DEFAULT_MEDICAL_SUPERVISOR_RATIO = 199;
const CONST_DEFAULT_TOTAL_PILGRIM_QUOTA = 2300;

export const PreferencesContext = createContext({});

const AGLO_CAMPAIGN_PAGES = [Urls.trip, Urls.invoice, Urls.dashboard, Urls.group, Urls.agency, Urls.assignation];

const PreferencesProvider = ({ children }) => {
  const { api } = useAPI();
  const { pathname } = useLocation();
  const writesInProgress = useIsMutating({ predicate: (mutation) => mutation.options.meta?.agloCampaignWrite });
  const { isAGLO, isConnected, user, activeOrganizationId, activeRole } = useGetUser();
  const campaignScope = `${user?.id || ''}:${activeOrganizationId || ''}:${activeRole || ''}`;
  const [selection, setSelection] = useState(null);
  const [campaignSwitch, setCampaignSwitch] = useState(null);

  const { data: preferences } = useQuery({
    queryFn: api.retrieve('preference', 'current/'),
    queryKey: ['preference/current/'],
  });

  const { data: availableCampaigns, isLoading: areCampaignsLoading } = useQuery({
    queryKey: ['campaign/', 'aglo-selector', campaignScope],
    enabled: !!(isConnected && isAGLO && user?.id && activeOrganizationId),
    onError: () => toast.error('Impossible de charger les campagnes.'),
    queryFn: api.list('campaign/'),
  });

  const {
    registration_deposit: registrationDeposit = CONST_DEFAULT_PRE_REGISTRATION_FEE,
    religious_supervisors_quota: religiousSupervisorsQuota = CONST_DEFAULT_RELIGIOUS_SUPERVISOR_QUOTA,
    medical_supervisors_quota: medicalSupervisorsQuota = CONST_DEFAULT_MEDICAL_SUPERVISOR_QUOTA,
    religious_supervisors_ratio: religiousSupervisorsRatio = CONST_DEFAULT_RELIGIOUS_SUPERVISOR_RATIO,
    medical_supervisors_ratio: medicalSupervisorsRatio = CONST_DEFAULT_MEDICAL_SUPERVISOR_RATIO,
    total_pilgrim_quota: totalPilgrimQuota = CONST_DEFAULT_TOTAL_PILGRIM_QUOTA,
    certificate_min_active_pilgrim: minPilgrimForAuthorization = CONST_DEFAULT_MINIMUM_PILGRIMS, // Minimum number of eligible (healthy and fully paid) pilgrims required for an authorization request by a SA
    pilgrim_eligibility_min_age: pilgrimEligibilityMinAge = CONST_ELIGIBILITY_AGE,
    campaign_year: campaignYear,
    campaign_registration_end_date: campaignRegistrationEndDate,
  } = preferences || {};

  const selectedCampaignYear = isAGLO && selection?.scope === campaignScope ? selection.year : campaignYear;
  const isHistoricalCampaign = !!(
    isAGLO &&
    campaignYear &&
    selectedCampaignYear &&
    Number(selectedCampaignYear) !== Number(campaignYear)
  );
  const isCampaignSwitching = !!(
    isAGLO &&
    campaignSwitch?.scope === campaignScope &&
    AGLO_CAMPAIGN_PAGES.includes(pathname)
  );

  useEffect(() => {
    if (!isConnected) {
      setSelection(null);
      setCampaignSwitch(null);
    } else if (!isAGLO || (campaignSwitch && campaignSwitch.scope !== campaignScope)) {
      setCampaignSwitch(null);
    }
  }, [campaignScope, campaignSwitch, isAGLO, isConnected]);

  const setSelectedCampaignYear = (year) => {
    if (
      !isAGLO ||
      !campaignYear ||
      isCampaignSwitching ||
      writesInProgress > 0 ||
      Number(year) === Number(selectedCampaignYear)
    )
      return;
    setCampaignSwitch({ scope: campaignScope, from: selectedCampaignYear, to: Number(year) });
    setSelection({ scope: campaignScope, year: Number(year) });
  };

  const finishCampaignSwitch = (year) => {
    setCampaignSwitch((previous) =>
      previous?.scope === campaignScope && previous.to === Number(year) ? null : previous
    );
  };

  const rejectCampaignSwitch = (year) => {
    if (campaignSwitch?.scope !== campaignScope || campaignSwitch.to !== Number(year)) return;
    setSelection({ scope: campaignScope, year: campaignSwitch.from });
    setCampaignSwitch(null);
  };

  const value = {
    preferences,
    registrationDeposit,
    religiousSupervisorsQuota,
    medicalSupervisorsQuota,
    campaignYear,
    selectedCampaignYear,
    setSelectedCampaignYear,
    availableCampaigns,
    areCampaignsLoading,
    isHistoricalCampaign,
    isCampaignSwitching,
    writesInProgress,
    campaignSwitch,
    finishCampaignSwitch,
    rejectCampaignSwitch,
    minPilgrimForAuthorization,
    pilgrimEligibilityMinAge,
    campaignRegistrationEndDate,
    totalPilgrimQuota,
    religiousSupervisorsRatio,
    medicalSupervisorsRatio,
  };

  return <PreferencesContext.Provider value={value}>{children}</PreferencesContext.Provider>;
};

PreferencesProvider.propTypes = {
  children: PropTypes.node,
};

export default PreferencesProvider;
